import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, Wrench, Truck, ArrowRight, Play, CheckCircle2,
  Shield, Clock, BarChart3, Users, Phone, Mail, X, UserPlus, LogIn,
  Zap, AlertTriangle, Activity, ShieldCheck, MapPin, FileCheck, Award,
  User, FileText, Sparkles, Droplets, ChevronRight, ShoppingBag, Anchor,
  Plug, PipetteIcon, HardHat, Package, ExternalLink, Star, DollarSign,
  Search, Loader2, Plus, Minus, Send, Leaf, Menu, ChevronDown, ShoppingCart, Camera, HeartPulse, FlaskConical, Waves, Hand, Droplet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import SEO, { orgSchema, localBusinessSchema } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import EntMegaMenuPanel from "@/components/EntMegaMenuPanel";
import QuickQuoteFormShared from "@/components/QuickQuoteFormShared";
import LiveInventoryEmbed from "@/components/LiveInventoryEmbed";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import LeadCaptureGate from "@/components/LeadCaptureGate";
import DemoPresentation from "@/components/DemoPresentation";
import MarketplaceSearchBar from "@/components/MarketplaceSearchBar";
import { HomeHeroV2, TrustMarquee, MarriageBento, IndustriesWeServe, AreasOfFocus } from "@/components/HomeHeroV2";
import { CapabilityTiles } from "@/components/CapabilityTiles";
import Reveal from "@/components/Reveal";

const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const STERILEMATE_LOGO = "/logos/sterilemate-white.png?v=4";

const IMAGES = {
  hospital: "https://images.unsplash.com/photo-1764885449345-d0d6629bf7bf?w=800&q=80",
  tools: "https://images.unsplash.com/photo-1568868053799-0728ed208ce4?w=800&q=80",
  medgas: "https://images.unsplash.com/photo-1766299892693-2370a8d47e23?w=800&q=80",
  toolbelt: "https://images.unsplash.com/photo-1454694220579-9d6672b1ec2a?w=800&q=80",
};

// Portal Access Modal
const PortalAccessModal = ({ isOpen, onClose, portalType, loginPath, registerPath, icon: Icon, color }) => {
  const navigate = useNavigate();
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700" data-testid="portal-access-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <DialogTitle className="text-xl font-semibold text-white">{portalType} Portal</DialogTitle>
          </div>
          <DialogDescription className="text-slate-400">Access the {portalType} command center</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <button onClick={() => { onClose(); navigate(loginPath); }} className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-[#003366] hover:bg-[#0056A8] text-white font-semibold rounded-lg transition-all" data-testid="modal-login-btn">
            <LogIn className="w-5 h-5" /> Login to Portal
          </button>
          <button onClick={() => { onClose(); navigate(registerPath); }} className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all" data-testid="modal-register-btn">
            <UserPlus className="w-5 h-5" /> Join the Network
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Collapsible row used in the mobile nav drawer
const MobileSection = ({ id, label, open, onToggle, children, testid }) => (
  <div className="rounded-lg overflow-hidden">
    <button
      type="button"
      onClick={() => onToggle(id)}
      className={`w-full flex items-center justify-between px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60 transition-colors ${open ? "bg-slate-800/60" : ""}`}
      aria-expanded={open}
      data-testid={testid}
    >
      <span className="font-medium">{label}</span>
      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <div className="pl-3 pr-1 py-1 ml-3 border-l border-slate-800 space-y-0.5" data-testid={`${testid}-panel`}>
        {children}
      </div>
    )}
  </div>
);

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const { cart: quoteCart, setShowCart: openQuoteCart } = useQuoteCart();
  const quoteCartCount = quoteCart.reduce((s, i) => s + (i.qty || 0), 0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [networkStats, setNetworkStats] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null); // which submenu is expanded
  const [mpMenuOpen, setMpMenuOpen] = useState(false); // top-nav Marketplace mega-menu (click-toggle)
  const mpMenuRef = useRef(null);
  const [quoteItems, setQuoteItems] = useState([{ product_name: "", sku: "", quantity: "", notes: "" }]);
  // On-the-go path: paste/upload one photo + an optional free-text note so
  // users in the field can fire off a request without filling every row.
  const [quoteImage, setQuoteImage] = useState(null);  // { url, filename, preview }
  const [quoteImageUploading, setQuoteImageUploading] = useState(false);
  const [quoteOnTheGoNote, setQuoteOnTheGoNote] = useState("");
  const [showOnTheGo, setShowOnTheGo] = useState(false);
  const [quoteContact, setQuoteContact] = useState({ name: "", email: "", phone: "", hospital: "" });
  const [quoteShipping, setQuoteShipping] = useState({ city: "", state: "", zip: "" });
  const [quoteFreight, setQuoteFreight] = useState({ use_hospital_courier: false, account: "" });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  // When a logged-in hospital user lands here (e.g. clicked "New Backorder"
  // from their dashboard), prefill the form and tag the resulting order with
  // their hospital_id so it appears in their dashboard list.
  const [hospitalContext, setHospitalContext] = useState(null);
  const navigate = useNavigate();

  // Close top-nav Marketplace mega-menu on outside click / Escape
  useEffect(() => {
    if (!mpMenuOpen) return;
    const handleClickOutside = (e) => {
      if (mpMenuRef.current && !mpMenuRef.current.contains(e.target)) setMpMenuOpen(false);
    };
    const handleEsc = (e) => { if (e.key === "Escape") setMpMenuOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [mpMenuOpen]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/network/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setNetworkStats(data); })
      .catch(() => {});
  }, []);

  // Auto-fill the quick-quote form when an authenticated hospital lands here
  // (e.g. clicked the "New Backorder" CTA from their dashboard sidebar).
  useEffect(() => {
    try {
      const token = localStorage.getItem("hospital_token");
      const userRaw = localStorage.getItem("hospital_user");
      const hsRaw = localStorage.getItem("hospital_health_system");
      if (!token || !userRaw) return;
      const user = JSON.parse(userRaw);
      const hs = hsRaw ? JSON.parse(hsRaw) : null;
      const hospitalName = hs?.name || hs?.hospital_name || user?.health_system_name || "";
      const hospitalId = hs?.id || user?.health_system_id || null;
      if (!hospitalId) return;
      setHospitalContext({
        id: hospitalId,
        name: hospitalName,
        contact_name: user?.name || "",
        contact_email: user?.email || "",
        contact_phone: user?.phone || "",
      });
      setQuoteContact((prev) => ({
        name: prev.name || user?.name || "",
        email: prev.email || user?.email || "",
        phone: prev.phone || user?.phone || "",
        hospital: prev.hospital || hospitalName,
      }));
    } catch (_e) { /* localStorage parse failure — ignore */ }
  }, []);

  // Scroll the Quick-Quote form into view whenever the page is reached via
  // the #quick-quote hash. The browser only auto-scrolls on a fresh load —
  // SPA navigations would otherwise leave the user at the top of the page.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#quick-quote") return;
    const tries = [80, 240, 600, 1200];
    const timeouts = tries.map((ms) =>
      setTimeout(() => {
        const el = document.getElementById("quick-quote");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, ms)
    );
    return () => timeouts.forEach(clearTimeout);
  }, []);

  const openPortalModal = (type) => { setSelectedPortal(type); setModalOpen(true); };
  const closePortalModal = () => { setModalOpen(false); setSelectedPortal(null); };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteContact.name || !quoteContact.email || !quoteContact.hospital) {
      toast.error("Please fill in your name, email, and hospital/facility name"); return;
    }
    const validItems = quoteItems.filter(i => i.product_name.trim());
    const hasOnTheGo = !!quoteImage?.url || !!quoteOnTheGoNote.trim();
    if (validItems.length === 0 && !hasOnTheGo) {
      toast.error("Add at least one product, paste a photo, or leave us a note");
      return;
    }
    setQuoteSubmitting(true);
    try {
      const shippingAddress = [
        quoteShipping.city.trim(),
        quoteShipping.state.trim(),
        quoteShipping.zip.trim(),
      ].filter(Boolean).join(", ");

      // Concatenate any pasted note into the supplier-facing notes block.
      const supplierNotes = [
        ...validItems.map(i => {
          const skuPart = i.sku ? ` · SKU: ${i.sku}` : "";
          const notePart = i.notes ? ` - ${i.notes}` : "";
          return `${i.product_name}${skuPart} (Qty: ${i.quantity || 1})${notePart}`;
        }),
        quoteOnTheGoNote.trim() ? `[On-the-go note] ${quoteOnTheGoNote.trim()}` : "",
        quoteImage?.url ? `[Photo attached] ${quoteImage.url}` : "",
      ].filter(Boolean).join("; ");

      const itemNameStr = validItems.length > 0
        ? validItems.map(i => i.sku ? `${i.product_name} (SKU: ${i.sku})` : i.product_name).join(", ")
        : (quoteOnTheGoNote.trim() ? `On-the-go: ${quoteOnTheGoNote.trim().slice(0, 80)}` : "Photo attached — see image");

      const totalQty = validItems.length > 0
        ? validItems.reduce((s, i) => s + (parseInt(i.quantity) || 1), 0)
        : 1;

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/order-request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_type: "backorder",
          hospital_id: hospitalContext?.id || null,
          hospital_name: quoteContact.hospital,
          address: shippingAddress,
          contact_name: quoteContact.name,
          contact_email: quoteContact.email,
          contact_phone: quoteContact.phone || "",
          item_name: itemNameStr,
          mfr_number: validItems.length === 1 ? (validItems[0].sku || "") : "",
          quantity: totalQty,
          notes: supplierNotes,
          items: validItems.map(i => ({
            name: i.product_name,
            sku: i.sku || "",
            quantity: parseInt(i.quantity) || 1,
            notes: i.notes || "",
          })),
          shipping_address: shippingAddress,
          use_hospital_courier: quoteFreight.use_hospital_courier,
          hospital_courier_account: quoteFreight.use_hospital_courier ? quoteFreight.account : "",
          ship_via: quoteFreight.use_hospital_courier ? "Hospital 3rd-Party Courier" : "Best Way",
          terms: "Net 30",
          image_url: quoteImage?.url || null,
        }),
      });
      if (res.ok) {
        let respData = {};
        try { respData = await res.json(); } catch (_e) { /* non-json */ }
        // If a logged-in hospital just submitted, deep-link them back to
        // their dashboard with the new backorder pinned/highlighted.
        if (hospitalContext?.id) {
          const newId = respData?.backorder_id || respData?.request_id;
          toast.success("Backorder submitted — taking you back to your dashboard...");
          setTimeout(() => {
            window.location.href = newId
              ? `/hospital/portal?tab=backorders&new=${encodeURIComponent(newId)}`
              : "/hospital/portal?tab=backorders";
          }, 900);
          return;
        }
        setQuoteSubmitted(true);
        toast.success("Request submitted! Our team will respond within 2 hours.");
      } else { toast.error("Failed to submit. Please try again or call 281.456.3458"); }
    } catch { toast.error("Network error. Please call us at 281.456.3458"); }
    finally { setQuoteSubmitting(false); }
  };

  const addQuoteItem = () => setQuoteItems([...quoteItems, { product_name: "", sku: "", quantity: "", notes: "" }]);
  const removeQuoteItem = (idx) => setQuoteItems(quoteItems.filter((_, i) => i !== idx));
  const updateQuoteItem = (idx, field, val) => { const items = [...quoteItems]; items[idx][field] = val; setQuoteItems(items); };

  // ----- On-the-go photo upload (file picker + paste) -----
  const uploadQuoteImage = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large — max 10 MB");
      return;
    }
    setQuoteImageUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/order-request/upload-image`, {
        method: "POST",
        body: fd,
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setQuoteImage({
        url: data.url,
        filename: data.filename,
        preview: URL.createObjectURL(file),
      });
      toast.success("Photo attached");
    } catch (err) {
      toast.error("Couldn't upload image — try again");
    } finally {
      setQuoteImageUploading(false);
    }
  };
  const onPasteQuoteImage = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        const blob = it.getAsFile();
        if (blob) {
          e.preventDefault();
          uploadQuoteImage(blob);
          return;
        }
      }
    }
  };

  const portalConfigs = {
    contractor: { type: "Contractor", loginPath: "/contractor/login", registerPath: "/contractor/register", icon: Wrench, color: "bg-emerald-600" },
    supplier: { type: "Supplier", loginPath: "/supplier/login", registerPath: "/supplier/register", icon: Truck, color: "bg-amber-600" },
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <SEO
        canonical="/"
        title="Hospital Supply Backorder Sourcing — Sterile Mate (TX, LA, AL, MS)"
        description="Sterile Mate solves hospital supply backorders across Texas, Louisiana, Alabama, and Mississippi. Sterile processing, EVS & janitorial, microfiber cleaning rags & mops, water purification, American Surgical neuro sponges, and healthcare supply chain — one dispatch center. Powered by AskIsaac."
        keywords={["hospital supply backorder Houston", "medical supply backorder Texas", "GPO alternative Louisiana"]}
        schema={{
          "@context": "https://schema.org",
          "@graph": [orgSchema, localBusinessSchema],
        }}
      />

      {/* ============ STICKY HEADER (corporate navy → blue gradient) ============ */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "shadow-lg shadow-black/30" : ""}`}
        style={{
          background: scrolled
            ? "linear-gradient(90deg, #050a16 0%, #0a1a35 30%, #0f2a5a 60%, #0056A8 100%)"
            : "linear-gradient(90deg, #050a16 0%, #0b1e3f 32%, #123a75 65%, #0056A8 100%)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "none",
        }}
      >
        {/* Left dark fade — keeps logos crisp */}
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 pointer-events-none w-[440px]"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,10,22,0.85) 0%, rgba(5,10,22,0.55) 45%, rgba(5,10,22,0) 100%)",
          }}
        />
        <div className={`relative max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between gap-3 md:gap-6 transition-all duration-300 ${scrolled ? "h-14 md:h-16" : "h-16 md:h-24"}`}>
          <Link to="/" className="flex items-center gap-2 md:gap-3 shrink-0 min-w-0" data-testid="header-logo">
            <img
              src={STERILEMATE_LOGO}
              alt="Sterile Mate"
              className={`w-auto shrink-0 object-contain transition-all duration-300 ${scrolled ? "h-9 md:h-12" : "h-12 md:h-16"}`}
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
            {/* How It Works dropdown — top-level overview + Backorder Sourcing for hospitals */}
            <div className="relative group" data-testid="header-howitworks-dropdown">
              <a href="#how-it-works" className="text-sm font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap flex items-center gap-1" data-testid="header-howitworks-link">
                How It Works
                <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" className="mt-0.5"><path d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"/></svg>
              </a>
              <div className="absolute left-0 top-full pt-2 w-[340px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-[#0b1d3a] border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
                  <Link to="/how-it-works" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })} className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800" data-testid="header-howitworks-overview">
                    <p className="text-sm font-semibold text-white">Platform Overview</p>
                    <p className="text-xs text-slate-400">QuickBid Exchange · 6-step explainer · how AskIsaac works end-to-end</p>
                  </Link>
                  <Link to="/how-it-works/backorder-sourcing" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })} className="block px-4 py-3 hover:bg-slate-800/60 group/item" data-testid="header-howitworks-backorder">
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l4-2 4 2 4-2 2 1z"/></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <p className="text-sm font-semibold text-white">Backorder Sourcing</p>
                          <span className="text-[9px] font-extrabold uppercase tracking-wider bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded">For Hospitals</span>
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed">How Sterile Mate hunts allocated SKUs on your behalf — 5-step procurement walkthrough.</p>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            <a href="#quick-quote" className="text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors whitespace-nowrap">Backorders</a>
            <Link
              to="/solutions/general-contracting"
              className="text-sm font-medium text-cyan-300 hover:text-cyan-200 transition-colors whitespace-nowrap"
              data-testid="landing-header-gc-link"
            >
              Healthcare Construction
            </Link>
            <Link to="/solutions" className="text-sm font-medium text-white/85 hover:text-white transition-colors">Solutions</Link>

            <a href="#network" className="text-sm font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap">Join Network</a>
            <Link
              to="/about"
              className="text-sm font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap"
              data-testid="landing-header-about-link"
            >
              About
            </Link>
            {/* Marketplace dropdown — click to open (also opens on hover for desktop) */}
            <div
              ref={mpMenuRef}
              className="relative"
              data-testid="header-marketplace-dropdown"
              onMouseEnter={() => setMpMenuOpen(true)}
            >
              <button
                type="button"
                onClick={() => setMpMenuOpen((v) => !v)}
                aria-expanded={mpMenuOpen}
                aria-haspopup="menu"
                className="text-sm font-medium text-white/85 hover:text-white transition-colors flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded"
                data-testid="header-marketplace-link"
              >
                Marketplace
                <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" className={`mt-0.5 transition-transform ${mpMenuOpen ? "rotate-180" : ""}`}><path d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"/></svg>
              </button>
              <div className={`absolute right-0 top-full pt-2 w-[820px] max-w-[95vw] transition-all z-50 ${mpMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}>
                <div className="bg-[#0b1d3a] border border-slate-700 rounded-xl shadow-2xl overflow-hidden" onClick={() => setMpMenuOpen(false)}>
                  <div className="grid grid-cols-2">
                    {/* Left column: Marketplace categories */}
                    <div className="border-r border-slate-800 max-h-[75vh] overflow-y-auto">
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">Marketplace</p>
                      </div>
                      <Link
                        key="mp-all"
                        to="/hospital/marketplace"
                        className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-all"
                      >
                        <p className="text-sm font-semibold text-white">All Marketplace Products</p>
                        <p className="text-xs text-slate-400">Filmop EVS · Janitorial · Disinfectants</p>
                      </Link>
                      <Link
                        key="mp-live-inventory"
                        to="/backorders/live-inventory"
                        className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-live-inventory"
                      >
                        <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Package className="w-5 h-5 text-slate-900" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">Weekly Deals / Backorder Items <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-slate-900 font-bold">LIVE</span></p>
                          <p className="text-xs text-slate-400">Live hard-to-find inventory · searchable any time</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-janitorial"
                        to="/marketplace/janitorial"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-janitorial"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3"/></svg>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Janitorial / EVS</p>
                            <span className="text-[9px] font-bold bg-cyan-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">11 Cats</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">ATP · Chemicals · Vacuums · Floor Care · CleanSite</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-microfiber"
                        to="/marketplace?category=evs-janitorial&sub=Microfiber"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-emerald-900/20 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-microfiber"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/></svg>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Microfiber Cleaning</p>
                            <span className="text-[9px] font-bold bg-emerald-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">70 SKUs</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Cloths · Wet mops · Dust mops · Pads · Dusters · Handles — Direct Mop Sales</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-home-health-aids"
                        to="/marketplace?category=home-health-aids"
                        className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-home-health-aids"
                      >
                        <p className="text-sm font-semibold text-white">Home Health Care Aids</p>
                        <p className="text-xs text-slate-400">BP monitors · diabetes · daily-living aids</p>
                      </Link>
                      <Link
                        key="mp-diabetes-management"
                        to="/solutions/diabetes-management"
                        className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-diabetes-management"
                      >
                        <p className="text-sm font-semibold text-white">
                          Diabetes Management
                          <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-sky-500 text-slate-900 font-bold align-middle">NEW</span>
                        </p>
                        <p className="text-xs text-slate-400">Glucose monitors · test strips · lancets</p>
                      </Link>
                      <Link
                        key="mp-blood-pressure-monitors"
                        to="/marketplace?category=blood-pressure-monitors"
                        className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-blood-pressure-monitors"
                      >
                        <p className="text-sm font-semibold text-white">Blood Pressure Monitors</p>
                        <p className="text-xs text-slate-400">Automatic · aneroid · ambulatory · pediatric cuffs</p>
                      </Link>
                      <Link
                        key="mp-filmop"
                        to="/marketplace?category=filmop-usa"
                        className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-filmop"
                      >
                        <p className="text-sm font-semibold text-white">Filmop USA</p>
                        <p className="text-xs text-slate-400">Alpha trolleys · frames · poles</p>
                      </Link>
                      <Link
                        key="mp-medical-devices"
                        to="/marketplace/medical-devices"
                        className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                        data-testid="header-marketplace-medical-devices"
                      >
                        <p className="text-sm font-semibold text-white flex items-center gap-2">
                          Medical Devices
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">New</span>
                        </p>
                        <p className="text-xs text-slate-400">BP Monitors · Blood Collection · Needles & Syringes</p>
                      </Link>
                      <Link
                        key="mp-gloves"
                        to="/marketplace/gloves"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-blue-900/20 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-gloves"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Hand className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Disposable Gloves</p>
                            <span className="text-[9px] font-bold bg-blue-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">49 SKUs</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Nitrile · Latex · Vinyl · Poly — AMMEX · Gloveworks · Microflex</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-furniture"
                        to="/marketplace/healthcare-examination-furniture"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-slate-800 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-furniture"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center flex-shrink-0 shadow-md">
                          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 11V6a3 3 0 016 0v5m-6 0h6m-6 0v10m6-10h4v10m-4 0h4M9 21H5m14-10V6a3 3 0 00-3-3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Healthcare Examination Furniture</p>
                            <span className="text-[9px] font-bold bg-slate-300 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">21 models</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Exam tables · Dental chairs · Treatment tables · Clinical stools</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-di-tank-exchange"
                        to="/marketplace/di-tank-exchange"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-cyan-900/20 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-di-tank-exchange"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-600 to-sky-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Droplets className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">DI Tank Exchange</p>
                            <span className="text-[9px] font-bold bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">$115+</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Next-day exchange · No contracts · cGMP / ISO 9001</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-hemodialysis"
                        to="/dialysis"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-teal-900/20 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-hemodialysis"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <HeartPulse className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Hemodialysis Product &amp; Care</p>
                            <span className="text-[9px] font-bold bg-teal-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">New</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Water · concentrates · additives · equipment · compliance</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-water-purification"
                        to="/marketplace?category=water-purification"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-sky-900/20 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-water-purification"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Waves className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Healthcare Water Purification Systems</p>
                            <span className="text-[9px] font-bold bg-sky-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">AAMI ST108</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Hydronix ROS-IXC · softeners · monitoring · RO/DI</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-sterile-processing"
                        to="/sterile-processing"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-fuchsia-900/20 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-sterile-processing"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
                          <FlaskConical className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Sterile Processing — MicroCare</p>
                            <span className="text-[9px] font-bold bg-fuchsia-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">New</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">ProEZ · ProChek · ProSpray · SPD compliance</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-greenklean"
                        to="/marketplace/green-klean"
                        className="flex items-start gap-3 px-4 py-3 hover:bg-emerald-900/20 border-b border-slate-800 transition-colors"
                        data-testid="header-marketplace-greenklean"
                      >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Leaf className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-white">Green Klean®</p>
                            <span className="text-[9px] font-bold bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded uppercase tracking-wide">New</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Chlorinated Disinfecting Tablets · EPA #71847-6-91038</p>
                        </div>
                      </Link>
                      <Link
                        key="mp-microbiology"
                        to="/microbiology-testing"
                        className="block px-4 py-3 hover:bg-slate-800/60"
                        data-testid="header-marketplace-microbiology"
                      >
                        <p className="text-sm font-semibold text-white">Microbiology Testing</p>
                        <p className="text-xs text-slate-400">A&amp;B Labs · ISO 17025 · Water + bio-burden panels</p>
                      </Link>
                    </div>
                    {/* Right column: Surgical + Sol-M + ENT (moved from standalone Surgical menu) */}
                    <div className="max-h-[75vh] overflow-y-auto">
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-[10px] font-semibold text-amber-300 uppercase tracking-wider">Surgical &amp; Clinical</p>
                      </div>
                      <Link to="/sterilemate/surgical" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">Surgical Marketplace</p>
                        <p className="text-xs text-slate-400">2,000+ SKUs · Aspen Medical, BluBox</p>
                      </Link>
                      <Link to="/marketplace/american-surgical" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800" data-testid="header-american-surgical-link">
                        <p className="text-sm font-semibold text-white flex items-center gap-2">
                          Neuro Patty Sponges
                          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">New</span>
                        </p>
                        <p className="text-xs text-slate-400">American Surgical · 124 SKUs · Made in Salem, MA</p>
                      </Link>
                      <Link to="/sterilemate/ent-surgical" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">ENT — BEAR-ENT</p>
                        <p className="text-xs text-slate-400">93+ SKUs · Ear · Nose · Throat + Sets</p>
                      </Link>
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">Sol-M Authorized Distributor</p>
                      </div>
                      {[
                        ["diabetes-care", "Diabetes Care"],
                        ["medication-delivery", "Medication Delivery"],
                        ["blood-collection", "Blood Collection"],
                        ["wound-care", "Wound Care"],
                        ["ppe", "PPE"],
                        ["vascular-access", "Vascular Access & Infusion"],
                        ["incontinence", "Incontinence"],
                        ["bulk-non-sterile", "Bulk Non-Sterile"],
                      ].map(([slug, name]) => (
                        <Link key={slug} to={`/sterilemate/${slug}`} className="block px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800/60" data-testid={`header-solm-${slug}`}>
                          {name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <a href="tel:2814563458" className="hidden 2xl:flex text-sm font-medium text-white/70 hover:text-white transition-colors items-center gap-1 whitespace-nowrap"><Phone className="w-3 h-3" />281.456.3458</a>
            <Link to="/hospital/login" className="hidden xl:inline-block text-sm font-medium text-white/80 hover:text-white transition-colors whitespace-nowrap" data-testid="hospital-login-btn">Hospital Login</Link>
            <Link to="/login" className="hidden xl:inline-block text-sm font-medium text-white/80 hover:text-white transition-colors whitespace-nowrap" data-testid="staff-login-btn">Staff Login</Link>
            {/* Quote Cart — visible on every page */}
            <button
              type="button"
              onClick={() => openQuoteCart(true)}
              className="relative inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-sm font-semibold whitespace-nowrap transition-colors backdrop-blur-sm"
              data-testid="header-cart-btn"
              aria-label={`Open quote cart (${quoteCartCount} items)`}
            >
              <ShoppingCart className="w-4 h-4 text-amber-400" />
              <span className="hidden sm:inline">Cart</span>
              {quoteCartCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full bg-amber-500 text-slate-900 text-[10px] font-black px-1.5" data-testid="header-cart-count">
                  {quoteCartCount}
                </span>
              )}
            </button>
            {/* Mobile hamburger trigger */}
            <button
              onClick={() => { setMobileMenuOpen(true); setMobileSection(null); }}
              aria-label="Open menu"
              className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* ============ MOBILE NAV DRAWER ============ */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="mobile-menu-drawer">
          {/* backdrop */}
          <button
            aria-label="Close menu backdrop"
            onClick={() => setMobileMenuOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          {/* panel */}
          <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-[#0a0f1a] border-l border-slate-800 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 h-16 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-7 w-auto" />
                <div className="w-px h-5 bg-slate-700" />
                <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-5 w-auto" />
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                className="w-10 h-10 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav className="px-3 py-4 space-y-1">
              {/* How It Works (collapsible) */}
              <MobileSection
                id="howitworks"
                label="How It Works"
                open={mobileSection === "howitworks"}
                onToggle={(id) => setMobileSection(mobileSection === id ? null : id)}
                testid="mobile-howitworks"
              >
                <Link to="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60" data-testid="mobile-howitworks-overview">Platform Overview</Link>
                <Link to="/how-it-works/backorder-sourcing" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60" data-testid="mobile-howitworks-backorder">Backorder Sourcing <span className="text-[9px] font-bold uppercase ml-1 bg-rose-500/20 text-rose-300 px-1.5 py-0.5 rounded">Hospitals</span></Link>
              </MobileSection>

              {/* Backorders (anchor) */}
              <a
                href="#quick-quote"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-lg text-amber-300 hover:bg-amber-500/10 font-semibold"
                data-testid="mobile-quickquote"
              >
                Backorders
                <ChevronRight className="w-4 h-4" />
              </a>

              {/* Solutions */}
              <Link
                to="/solutions"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60"
                data-testid="mobile-solutions"
              >
                Solutions
                <ChevronRight className="w-4 h-4" />
              </Link>

              {/* Surgical (collapsible) */}
              <MobileSection
                id="surgical"
                label="Surgical"
                open={mobileSection === "surgical"}
                onToggle={(id) => setMobileSection(mobileSection === id ? null : id)}
                testid="mobile-surgical"
              >
                <Link to="/sterilemate/surgical" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60 font-semibold">Surgical Marketplace</Link>
                <Link to="/marketplace/american-surgical" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60 font-semibold flex items-center gap-2" data-testid="mobile-american-surgical">
                  <span>Neuro Patty Sponges</span>
                  <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">New</span>
                </Link>
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">Sol-M Distributor</p>
                {[
                  ["diabetes-care", "Diabetes Care"],
                  ["medication-delivery", "Medication Delivery"],
                  ["blood-collection", "Blood Collection"],
                  ["wound-care", "Wound Care"],
                  ["ppe", "PPE"],
                  ["vascular-access", "Vascular Access & Infusion"],
                  ["incontinence", "Incontinence"],
                  ["bulk-non-sterile", "Bulk Non-Sterile"],
                ].map(([slug, name]) => (
                  <Link key={slug} to={`/sterilemate/${slug}`} onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid={`mobile-solm-${slug}`}>
                    {name}
                  </Link>
                ))}
                <p className="px-3 pt-3 pb-1 text-[10px] font-semibold text-amber-300 uppercase tracking-wider">ENT — BEAR-ENT</p>
                <Link to="/marketplace/bear-ent" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60">All ENT Instruments (208+)</Link>
              </MobileSection>

              {/* Marketplace (collapsible) */}
              <MobileSection
                id="marketplace"
                label="Marketplace"
                open={mobileSection === "marketplace"}
                onToggle={(id) => setMobileSection(mobileSection === id ? null : id)}
                testid="mobile-marketplace"
              >
                <Link to="/hospital/marketplace" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60 font-semibold">All Marketplace Products</Link>
                <Link to="/backorders/live-inventory" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-200 hover:bg-slate-800/60 font-semibold" data-testid="mobile-marketplace-live-inventory">
                  <span className="inline-flex items-center gap-2"><Package className="w-3.5 h-3.5 text-amber-400" /> Weekly Deals / Backorder List</span>
                  <span className="text-[9px] font-bold bg-amber-500 text-slate-900 px-1.5 py-0.5 rounded uppercase">Live</span>
                </Link>
                <Link to="/solutions/diabetes-management" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-diabetes-management">
                  <span className="inline-flex items-center gap-2"><Droplet className="w-3.5 h-3.5 text-sky-400" /> Diabetes Management</span>
                  <span className="text-[9px] font-bold bg-sky-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">New</span>
                </Link>
                <Link to="/marketplace/janitorial" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-janitorial">
                  <span>Janitorial / EVS</span>
                  <span className="text-[9px] font-bold bg-cyan-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">11 Cats</span>
                </Link>
                <Link to="/marketplace/di-tank-exchange" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-di-tank-exchange">
                  <span className="inline-flex items-center gap-2"><Droplets className="w-3.5 h-3.5 text-cyan-400" /> DI Tank Exchange</span>
                  <span className="text-[9px] font-bold bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">$115+</span>
                </Link>
                <Link to="/dialysis" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-hemodialysis">
                  <span className="inline-flex items-center gap-2"><HeartPulse className="w-3.5 h-3.5 text-teal-400" /> Hemodialysis Product &amp; Care</span>
                  <span className="text-[9px] font-bold bg-teal-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">New</span>
                </Link>
                <Link to="/marketplace?category=water-purification" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-water-purification">
                  <span className="inline-flex items-center gap-2"><Waves className="w-3.5 h-3.5 text-sky-400" /> Healthcare Water Purification</span>
                  <span className="text-[9px] font-bold bg-sky-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">ST108</span>
                </Link>
                <Link to="/sterile-processing" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-sterile-processing">
                  <span className="inline-flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5 text-fuchsia-400" /> Sterile Processing — MicroCare</span>
                  <span className="text-[9px] font-bold bg-fuchsia-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">New</span>
                </Link>
                <Link to="/marketplace/green-klean" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-greenklean">
                  <span className="inline-flex items-center gap-2"><Leaf className="w-3.5 h-3.5 text-emerald-400" /> Green Klean®</span>
                  <span className="text-[9px] font-bold bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">New</span>
                </Link>
                <Link to="/marketplace/gloves" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-gloves">
                  <span className="inline-flex items-center gap-2"><Hand className="w-3.5 h-3.5 text-blue-400" /> Disposable Gloves</span>
                  <span className="text-[9px] font-bold bg-blue-400 text-slate-900 px-1.5 py-0.5 rounded uppercase">49 SKUs</span>
                </Link>
                <Link to="/marketplace/healthcare-examination-furniture" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60" data-testid="mobile-marketplace-furniture">
                  <span className="inline-flex items-center gap-2"><svg className="w-3.5 h-3.5 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 11V6a3 3 0 016 0v5m-6 0h6m-6 0v10m6-10h4v10m-4 0h4M9 21H5m14-10V6a3 3 0 00-3-3" strokeLinecap="round" strokeLinejoin="round"/></svg> Healthcare Furniture</span>
                  <span className="text-[9px] font-bold bg-slate-300 text-slate-900 px-1.5 py-0.5 rounded uppercase">21 models</span>
                </Link>
              </MobileSection>

              {/* Join Network (anchor) */}
              <a
                href="#network"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60"
                data-testid="mobile-join-network"
              >
                Join Network
                <ChevronRight className="w-4 h-4" />
              </a>

              {/* About */}
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60"
                data-testid="mobile-about-link"
              >
                About
                <ChevronRight className="w-4 h-4" />
              </Link>
            </nav>

            <div className="border-t border-slate-800 px-5 py-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Link to="/hospital/login" onClick={() => setMobileMenuOpen(false)} className="text-center px-3 py-2.5 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-medium" data-testid="mobile-hospital-login">Hospital Login</Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="text-center px-3 py-2.5 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-medium" data-testid="mobile-staff-login">Staff Login</Link>
              </div>
              <a href="tel:2814563458" className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white text-sm" data-testid="mobile-phone">
                <Phone className="w-4 h-4" /> 281.456.3458
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ============ HERO (V2) + TRUST MARQUEE + MARRIAGE BENTO + VS DISTRIBUTORS ============ */}
      <HomeHeroV2
        onRequestDemo={() => setShowLeadCapture(true)}
        onJoinNetwork={() => navigate('/join-network')}
      />

      {/* ============ NEW: HEALTHCARE GENERAL CONTRACTING CALLOUT STRIP ============ */}
      <Link
        to="/solutions/general-contracting"
        className="group block bg-gradient-to-r from-amber-500 via-amber-400 to-cyan-500 border-y-2 border-amber-600/40 hover:brightness-105 transition-all"
        data-testid="home-gc-callout-strip"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-800 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600 animate-pulse" />
            New
          </span>
          <p className="text-sm md:text-base font-bold text-slate-900">
            Sterile Mate Healthcare General Contracting — capital projects, room retrofits &amp; emergency break-fix.
          </p>
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-900 group-hover:translate-x-1 transition-transform">
            Learn more <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>

      <CapabilityTiles />

      <AreasOfFocus />

      {/* ============ QUICK QUOTE / BACKORDER SEARCH (primary above-fold capture) ============ */}
      <section id="quick-quote" className="py-14 bg-gradient-to-b from-[#142a4d] via-[#0d2042] to-[#0a1c3c] border-t border-amber-400/10" data-testid="quick-quote-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/15 border border-amber-400/40 rounded-full mb-4">
              <Search className="w-3.5 h-3.5 text-amber-300" />
              <span className="text-xs font-bold text-amber-200 uppercase tracking-wider">Backorders &mdash; Hard-to-Find SKUs</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white tracking-tight mb-3">
              America&apos;s <span className="text-amber-400">#1 Source</span> for Hard-to-Find Medical Supplies.
            </h2>
            <p className="text-base text-slate-300 max-w-xl mx-auto">
              List anything on backorder, allocation, or discontinued. <strong className="text-white">Pricing &amp; availability in 2 hours.</strong>
            </p>
          </Reveal>

          <div className="max-w-4xl mx-auto">
            <QuickQuoteFormShared
              hospitalContext={hospitalContext}
              // Only intercept the submission for logged-in hospitals (deep-link back
              // to their dashboard). For PUBLIC / unauthenticated submitters we let
              // QuickQuoteFormShared render its own inline success card, which shows
              // the "Searching for stock…" spinner that polls the supplier-discovery
              // result and then displays the "verified / unverified / no source" message.
              onSubmitted={hospitalContext?.id ? (respData) => {
                const newId = respData?.backorder_id || respData?.request_id;
                toast.success("Backorder submitted — taking you back to your dashboard...");
                setTimeout(() => {
                  window.location.href = newId
                    ? `/hospital/portal?tab=backorders&new=${encodeURIComponent(newId)}`
                    : "/hospital/portal?tab=backorders";
                }, 900);
              } : null}
            />
          </div>

          {/* Live Backorder List — preview under the form. Gets the FULL 6xl
              container width so descriptions don't wrap into 6-line towers. */}
          <div className="mt-10">
            <LiveInventoryEmbed variant="compact" />
          </div>
        </div>
      </section>

      <TrustMarquee />

      {/* ============ CERTIFICATIONS & NETWORK TRUST STRIP ============ */}
      <section className="bg-white border-y border-slate-200" data-testid="trust-wall-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-9">
          <p className="text-[10px] font-black uppercase tracking-[2.5px] text-slate-500 text-center mb-6">
            Certified · Approved · Verified
          </p>
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-x-4 gap-y-6 items-center">
            {[
              { code: "small business",       label: "State of TX small business" },
              { code: "small business",       label: "Minority Business" },
              { code: "TMC",       label: "Texas Medical Center" },
              { code: "9GH66",     label: "CAGE Code" },
              { code: "DUNS",      label: "118062870" },
              { code: "ICRA",      label: "ICRA Compliant" },
              { code: "ST108",     label: "AAMI ST108 Ready" },
              { code: "JC",        label: "Joint Commission Ready" },
              { code: "OSHA",      label: "Healthcare Certified" },
            ].map((c) => (
              <div key={c.code} className="text-center group">
                <div className="mx-auto h-14 w-14 rounded-full border-2 border-slate-300 flex items-center justify-center bg-slate-50 group-hover:border-blue-600 group-hover:bg-blue-50 transition-colors">
                  <span className="text-[10px] font-black tracking-[1px] text-slate-600 group-hover:text-blue-700">{c.code}</span>
                </div>
                <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 leading-tight">{c.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-400 text-center max-w-2xl mx-auto italic">
            State of Texas Certified Small Business supplier · Texas Medical Center–based ·
            Registered federal contractor (CAGE 9GH66 · DUNS 118062870).
          </p>
        </div>
      </section>

      {/* ============ QUICK QUOTE moved up to above the Credentials · Clients · Compliance marquee ============ */}

      {/* ============ SHOP-BY-CATEGORY PRODUCT RAIL (visual pull-through to Marketplace) ============ */}
      <section className="py-16 bg-white" data-testid="marketplace-rail-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-3">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[2.5px] text-blue-700 mb-2">
                Marketplace · Ready to Ship
              </p>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight">
                Real product, real inventory, real fill rates.
              </h2>
              <p className="mt-2 text-slate-600 max-w-xl">
                Ansell, AMMEX, DeRoyal, American Surgical, Brewer &amp; Ritter — direct-brand and on-the-ground stock across a curated 3,000+ SKU catalog.
              </p>
            </div>
            <Link
              to="/marketplace"
              data-testid="rail-view-all"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 self-start md:self-auto"
            >
              Browse full marketplace →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              {
                title: "Ansell Nitrile Gloves",
                subtitle: "513 SKUs · HyFlex · MICROFLEX",
                img: "/api/static/gloves/ammex-indigo-nitrile-exam.png",
                to: "/marketplace/gloves/ansell",
                accent: "from-blue-500 to-indigo-700",
                local: true,
              },
              {
                title: "AMMEX Exam Gloves",
                subtitle: "27 SKUs · Same-week dispatch",
                img: "/api/static/gloves/ammex-blue-nitrile-exam.png",
                to: "/marketplace/gloves",
                accent: "from-slate-700 to-slate-900",
                local: true,
              },
              {
                title: "Examination Furniture",
                subtitle: "21 models · Brewer · Ritter",
                img: "/api/static/furniture/access-high-low-power-exam-table.jpg",
                to: "/marketplace/healthcare-examination-furniture",
                accent: "from-emerald-600 to-teal-800",
                local: true,
              },
              {
                title: "Backorder Sourcing",
                subtitle: "On-The-Ground Stock · 24-hr quote",
                img: "/api/static/furniture/9500-series.jpg",
                to: "/hospital/backorders",
                accent: "from-amber-500 to-orange-700",
                local: true,
              },
              {
                title: "American Surgical",
                subtitle: "Neuro sponges · Sterile OR supplies",
                img: "https://www.americansurgical.com/wp-content/uploads/2023/06/Ray-Cot-NEW23-1024x707.png",
                to: "/marketplace",
                accent: "from-red-600 to-rose-800",
              },
            ].map((tile) => (
              <Link
                key={tile.title}
                to={tile.to}
                data-testid={`rail-tile-${tile.title.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                className="group relative overflow-hidden rounded-xl bg-slate-100 aspect-[3/4] block border border-slate-200 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              >
                <img
                  src={tile.local ? `${process.env.REACT_APP_BACKEND_URL}${tile.img}` : tile.img}
                  alt={tile.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${tile.accent} opacity-70 group-hover:opacity-80 transition-opacity`} />
                <div className="absolute inset-0 flex flex-col justify-end p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/80 mb-1">Marketplace</p>
                  <h3 className="text-white font-black text-lg leading-tight">{tile.title}</h3>
                  <p className="text-white/90 text-xs mt-1">{tile.subtitle}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-white text-xs font-bold opacity-90 group-hover:opacity-100">
                    Browse →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Backorder / Quick Quote section moved above the marketplace rail ============ */}

      {/* SolutionsGrid + MetricsBand removed — SolutionsGrid overlapped
          heavily with <AreasOfFocus /> above the fold, and MetricsBand's
          208+/536/37s/5+yr vanity strip was flagged as redundant. */}
      <MarriageBento />
      <IndustriesWeServe />

      {/* ============ QUICK QUOTE moved up to right after TrustMarquee ============ */}

      {/* ============ HOW IT WORKS ============ */}
      <section id="how-it-works" className="py-24 bg-gradient-to-b from-[#0f3460] via-[#0c2d52] to-[#0a1e3d]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-16">
            <p className="text-xs font-semibold text-cyan-400 uppercase tracking-[0.2em] mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
              From Request to Resolution
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Whether you need a plumber at 2 AM or an electrician for a planned shutdown — AskIsaac dispatches the right trade, every time.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-20 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-cyan-500/20 via-cyan-400/60 to-cyan-500/20" />

            {[
              {
                num: "01",
                title: "Facility Posts a Request",
                desc: "A pipe bursts near sterile processing. Your facilities team texts AskIsaac or submits through the portal. No paperwork. No phone tag.",
                icon: Building2,
                color: "bg-blue-500/10 border-blue-500/30",
                iconColor: "text-blue-400",
                numColor: "text-blue-500/20",
              },
              {
                num: "02",
                title: "AskIsaac Matches & Dispatches",
                desc: "Our intelligent engine matches the job to vetted, licensed contractors in your area. Competitive blind bidding ensures the best price and fastest response.",
                icon: Zap,
                color: "bg-amber-500/10 border-amber-500/30",
                iconColor: "text-amber-400",
                numColor: "text-amber-500/20",
              },
              {
                num: "03",
                title: "Work Completed & Documented",
                desc: "Contractor arrives, completes the job, uploads photos. GPS-verified. Compliance docs generated automatically. Audit-ready from day one.",
                icon: CheckCircle2,
                color: "bg-emerald-500/10 border-emerald-500/30",
                iconColor: "text-emerald-400",
                numColor: "text-emerald-500/20",
              },
            ].map((step, idx) => (
              <Reveal key={step.num} delay={idx * 0.12}>
                <div className={`relative border ${step.color} rounded-xl p-8 hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 backdrop-blur-sm`} data-testid={`how-it-works-step-${step.num}`}>
                  <span className={`absolute top-4 right-4 text-6xl font-black ${step.numColor} select-none`}>{step.num}</span>
                  <div className={`relative z-10 w-12 h-12 ${step.color} rounded-lg flex items-center justify-center mb-5`}>
                    <step.icon className={`w-6 h-6 ${step.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 text-center space-y-4">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-full">
              <Clock className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-slate-300">Average dispatch time: under 30 minutes</span>
            </div>
            <div>
              <Link to="/how-it-works" data-testid="how-it-works-learn-more">
                <Button variant="outline" className="border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/10 h-11 px-6 font-semibold">
                  Learn more about the platform
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHO WE SERVE — DUAL AUDIENCE ============ */}
      <section className="py-24 bg-gradient-to-b from-[#0a1e3d] via-[#0d1a33] to-[#0a1628]" data-testid="who-we-serve-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-12">
            <p className="text-xs font-semibold text-cyan-400 uppercase tracking-[0.2em] mb-3">Who We Serve</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
              Built for Both Sides
            </h2>
          </Reveal>

          <Tabs defaultValue="facilities" className="max-w-5xl mx-auto">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-10 bg-slate-800/50 border border-slate-700/50 p-1 rounded-lg h-12">
              <TabsTrigger value="facilities" className="rounded-md text-sm font-semibold text-slate-400 data-[state=active]:bg-[#003366] data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="tab-facilities">
                <Building2 className="w-4 h-4 mr-2" /> For Facilities
              </TabsTrigger>
              <TabsTrigger value="contractors" className="rounded-md text-sm font-semibold text-slate-400 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="tab-contractors">
                <Wrench className="w-4 h-4 mr-2" /> For Contractors
              </TabsTrigger>
            </TabsList>

            {/* Facilities Tab */}
            <TabsContent value="facilities">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Stop chasing contractors. Start dispatching them.</h3>
                  <p className="text-slate-400 mb-6">Your facilities team shouldn&apos;t spend hours on the phone finding a licensed plumber at midnight. AskIsaac puts a vetted network at your fingertips.</p>
                  <div className="space-y-4 mb-8">
                    {[
                      "One-click dispatch for electrical, plumbing & construction",
                      "Competitive blind bidding — best price wins",
                      "Real-time GPS tracking and photo verification",
                      "Automatic compliance documentation for Joint Commission",
                      "24/7 emergency response network",
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-300">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Button onClick={() => setShowLeadCapture(true)} className="bg-[#0056A8] hover:bg-[#003366] text-white rounded-lg px-6 h-11 shadow-lg shadow-blue-900/30" data-testid="facilities-demo-btn">
                    <Play className="w-4 h-4 mr-2" /> Schedule a Demo
                  </Button>
                </div>
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                  <div className="space-y-3">
                    {[
                      { icon: Plug, title: "Electrical", desc: "Troubleshooting, upgrades, emergency power, planned shutdowns, 24/7 service calls", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
                      { icon: Droplets, title: "Plumbing", desc: "Sewage repairs, water leaks, medical gas systems, backflow prevention", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
                      { icon: HardHat, title: "Construction", desc: "Healthcare buildouts, renovations, ICRA-compliant projects, tenant improvements", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
                      { icon: Package, title: "Supplies", desc: "Medical supplies, janitorial products, water purification systems", color: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/30" },
                    ].map((svc) => (
                      <div key={svc.title} className={`flex items-start gap-4 p-4 border ${svc.bg} rounded-lg`}>
                        <div className={`w-10 h-10 ${svc.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <svc.icon className={`w-5 h-5 ${svc.color}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">{svc.title}</p>
                          <p className="text-xs text-slate-400">{svc.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Contractors Tab */}
            <TabsContent value="contractors">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Get hospital work without cold calling.</h3>
                  <p className="text-slate-400 mb-6">Join the AskIsaac network and receive job requests from healthcare facilities across the Houston metro. Licensed electricians, plumbers, and GCs — this is your pipeline.</p>
                  <div className="space-y-4 mb-8">
                    {[
                      "Receive job alerts matched to your trade and location",
                      "Submit competitive bids through blind bidding",
                      "Build your Fortress Certification — stand out to facilities",
                      "Get paid faster with digital work orders and verification",
                      "Grow your healthcare portfolio with verified references",
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-300">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => openPortalModal("contractor")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6 h-11" data-testid="contractor-join-btn">
                      <UserPlus className="w-4 h-4 mr-2" /> Join the Network
                    </Button>
                    <Button variant="outline" onClick={() => openPortalModal("supplier")} className="border-slate-600 text-slate-300 hover:bg-slate-800 rounded-lg px-6 h-11" data-testid="supplier-join-btn">
                      <Truck className="w-4 h-4 mr-2" /> Supplier Portal
                    </Button>
                  </div>
                </div>
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                  <div className="space-y-3">
                    {[
                      { icon: Wrench, title: "Electricians", desc: "Medical-grade installations, troubleshooting, emergency calls, panel upgrades", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
                      { icon: Droplets, title: "Plumbers", desc: "Medical gas, dialysis water supply, sewage repairs, water leaks, backflow", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
                      { icon: HardHat, title: "General Contractors", desc: "Healthcare buildouts, department renovations, compliance-driven upgrades", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
                      { icon: Truck, title: "Suppliers", desc: "Medical supplies, janitorial products, water purification equipment, PPE", color: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/30" },
                    ].map((svc) => (
                      <div key={svc.title} className={`flex items-start gap-4 p-4 border ${svc.bg} rounded-lg`}>
                        <div className={`w-10 h-10 ${svc.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <svc.icon className={`w-5 h-5 ${svc.color}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">{svc.title}</p>
                          <p className="text-xs text-slate-400">{svc.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* ============ COMMAND CENTERS ============ */}
      <section id="network" className="py-24 bg-gradient-to-b from-[#0a1628] via-[#0d1526] to-[#0a0f1a]" data-testid="command-centers-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-cyan-400 uppercase tracking-[0.2em] mb-3">Your Portal</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
              Three Portals. One Network.
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Every stakeholder gets their own command center — connected through AskIsaac&apos;s intelligent dispatch engine.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Building2,
                title: "Hospital Portal",
                subtitle: "Facility Operations small business",
                desc: "Post work orders, track projects, manage compliance, and access your full vendor network in one place.",
                features: ["One-Click Dispatch", "Live Project Tracking", "Compliance Dashboard", "Competitive Bidding"],
                borderColor: "border-blue-500/30 hover:border-blue-400/60",
                iconBg: "bg-blue-500/10",
                iconColor: "text-blue-400",
                accentColor: "text-blue-400",
                onClick: () => navigate("/hospital/login"),
              },
              {
                icon: Wrench,
                title: "Contractor Portal",
                subtitle: "Your Digital Storefront",
                desc: "Receive matched job alerts, submit bids, manage credentials, and earn your Fortress Certification.",
                features: ["Job Alert Feed", "Bid Management", "Credential Vault", "Fortress Certification"],
                borderColor: "border-emerald-500/30 hover:border-emerald-400/60",
                iconBg: "bg-emerald-500/10",
                iconColor: "text-emerald-400",
                accentColor: "text-emerald-400",
                onClick: () => openPortalModal("contractor"),
              },
              {
                icon: Truck,
                title: "Supplier Portal",
                subtitle: "Quote & Fulfillment small business",
                desc: "Respond to RFQs, manage inventory, fulfill orders, and grow your healthcare customer base.",
                features: ["RFQ Notifications", "Competitive Bidding", "Order Management", "Network Analytics"],
                borderColor: "border-amber-500/30 hover:border-amber-400/60",
                iconBg: "bg-amber-500/10",
                iconColor: "text-amber-400",
                accentColor: "text-amber-400",
                onClick: () => openPortalModal("supplier"),
              },
            ].map((portal) => (
              <div key={portal.title} className={`bg-slate-800/50 border ${portal.borderColor} rounded-xl p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/20 cursor-pointer`} onClick={portal.onClick} data-testid={`portal-${portal.title.toLowerCase().replace(" ", "-")}`}>
                <div className={`w-12 h-12 ${portal.iconBg} rounded-lg flex items-center justify-center mb-5`}>
                  <portal.icon className={`w-6 h-6 ${portal.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">{portal.title}</h3>
                <p className={`text-sm font-medium ${portal.accentColor} mb-3`}>{portal.subtitle}</p>
                <p className="text-sm text-slate-400 mb-5">{portal.desc}</p>
                <div className="space-y-2 mb-6">
                  {portal.features.map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <CheckCircle2 className={`w-4 h-4 ${portal.accentColor}`} />
                      <span className="text-sm text-slate-300">{f}</span>
                    </div>
                  ))}
                </div>
                <div className={`flex items-center gap-2 ${portal.accentColor} font-semibold text-sm`}>
                  Enter Portal <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/login" className="text-sm font-medium text-slate-500 hover:text-white transition-colors flex items-center justify-center gap-1" data-testid="admin-staff-login">
              <Shield className="w-3.5 h-3.5" /> Staff / Admin Login
            </Link>
          </div>
        </div>
      </section>

      {/* ============ STATS BAR ============ */}
      <section className="py-14 bg-gradient-to-r from-[#003366] via-[#0056A8] to-[#003366] border-y border-blue-500/20" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "2-Hr", label: "STAT Response" },
              { value: "Live", label: "GPS Tracking" },
              { value: "Blind", label: "Competitive Bidding" },
              { value: "24/7", label: "Emergency Calls" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-4xl md:text-5xl font-black text-white">{stat.value}</p>
                <p className="text-sm text-blue-200 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW CONTRACTORS EARN ============ */}
      <section className="py-24 bg-gradient-to-b from-[#0a0f1a] via-[#0d1526] to-[#0a1628]" data-testid="how-contractors-earn">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-emerald-400 uppercase tracking-[0.2em] mb-3">For Contractors & Suppliers</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
              How You Earn With Sterile Mate
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              We&apos;re building an army of licensed trades and suppliers. You do the work — we handle the dispatch, compliance, and payments.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { num: "01", title: "Register", desc: "Sign up in 2 minutes with your trade, license, and service area. Free to join.", icon: UserPlus, color: "border-cyan-500/30 bg-cyan-500/10", iconColor: "text-cyan-400" },
              { num: "02", title: "Get Matched", desc: "AskIsaac matches you with hospital jobs based on your trade, location, and certification tier.", icon: Zap, color: "border-amber-500/30 bg-amber-500/10", iconColor: "text-amber-400" },
              { num: "03", title: "Complete Work", desc: "Arrive on-site, do the work, upload photos. GPS-verified. Compliance docs generated automatically.", icon: CheckCircle2, color: "border-emerald-500/30 bg-emerald-500/10", iconColor: "text-emerald-400" },
              { num: "04", title: "Get Paid", desc: "Submit your invoice through the platform. Fast payment. Build your healthcare portfolio and earn your next tier.", icon: DollarSign, color: "border-violet-500/30 bg-violet-500/10", iconColor: "text-violet-400" },
            ].map((step) => (
              <div key={step.num} className={`relative border ${step.color} rounded-xl p-6 hover:-translate-y-1 transition-all duration-200`}>
                <span className="absolute top-3 right-3 text-4xl font-black text-white/5 select-none">{step.num}</span>
                <div className={`w-10 h-10 ${step.color} rounded-lg flex items-center justify-center mb-4`}>
                  <step.icon className={`w-5 h-5 ${step.iconColor}`} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Network Stats Banner */}
          {networkStats && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 mb-8" data-testid="network-stats-banner">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">The Sterile Mate Network</h3>
                  <p className="text-sm text-slate-400">Join a growing network of healthcare trades and suppliers</p>
                </div>
                <div className="flex items-center gap-8">
                  {[
                    { value: networkStats.contractors, label: "Contractors", color: "text-emerald-400" },
                    { value: networkStats.suppliers, label: "Suppliers", color: "text-amber-400" },
                    { value: networkStats.health_systems, label: "Health Systems", color: "text-cyan-400" },
                    { value: networkStats.states_served, label: "States", color: "text-violet-400" },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-slate-500">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="text-center">
            <Link to="/join-network">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-8 h-12 text-base font-semibold" data-testid="earn-join-btn">
                <UserPlus className="w-5 h-5 mr-2" /> Join the Network — It&apos;s Free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FEATURED CONTRACTORS — removed per user request ============ */}

      {/* OUR STORY / CREDIBILITY section removed per request — the About page
          carries the full founder narrative and credibility markers. */}

      {/* ============ EXPLORE CAPABILITIES — removed per user request ============ */}

      {/* CTA "Ready to Get Started?" section and AskIsaac "About the Platform"
          disclaimer removed per request. Footer follows the story section directly. */}

      {/* Inline mini-footer removed — the site-wide <Footer /> below already
          covers logos, platform links, portals, contact, and legal. */}

      {/* ============ MODALS ============ */}
      {selectedPortal && portalConfigs[selectedPortal] && (
        <PortalAccessModal
          isOpen={modalOpen}
          onClose={closePortalModal}
          portalType={portalConfigs[selectedPortal].type}
          loginPath={portalConfigs[selectedPortal].loginPath}
          registerPath={portalConfigs[selectedPortal].registerPath}
          icon={portalConfigs[selectedPortal].icon}
          color={portalConfigs[selectedPortal].color}
        />
      )}

      <LeadCaptureGate open={showLeadCapture} onClose={() => setShowLeadCapture(false)} onDemoStart={() => setShowDemo(true)} />
      <DemoPresentation open={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
}
